import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, CurrencyDollarIcon, ChevronDownIcon, AdjustmentsHorizontalIcon, PhotoIcon, PlusIcon } from '@heroicons/react/24/outline';
import { IImageGeneration, IUserModel, AspectRatioLabels, ResolutionLabels, Resolution, AspectRatio, getLabelsByLocale, Locale } from '@packages/global';
import { useCoinStore } from '../../stores/coinStore';
import ImageUpload from '../common/ImageUpload';
import ImageTabs from '../common/ImageTabs';
import { ModelSelector } from '../common/form';
import MediaPreviewModal from '../common/MediaPreviewModal';

interface ImageEditFormData {
  prompt: string;
  modelId: string;
  resolution: Resolution;
  aspectRatio: AspectRatio;
  referenceImages: string[];
}

interface ImageEditFormProps {
  editingItem: IImageGeneration | null;
  formData: ImageEditFormData;
  models: IUserModel[];
  modelsLoading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onFormDataChange: (data: Partial<ImageEditFormData>) => void;
  onAspectRatioChange: (ratio: AspectRatio) => void;
  onResolutionChange?: (resolution: Resolution) => void;
  onReset: () => void;
}

export default function ImageEditForm({
  editingItem,
  formData,
  models,
  modelsLoading,
  onSubmit,
  onFormDataChange,
  onAspectRatioChange,
  onResolutionChange,
  onReset
}: ImageEditFormProps) {
  const { t, i18n } = useTranslation(['generation', 'common_ui']);
  const { account } = useCoinStore();
  const selectedModel = models.find(m => m.id === formData.modelId);
  const resolutionLabels = getLabelsByLocale(ResolutionLabels, i18n.language as Locale);
  const aspectRatioLabels = getLabelsByLocale(AspectRatioLabels, i18n.language as Locale);

  // 下拉选择器状态
  const [showResolutionSelector, setShowResolutionSelector] = useState(false);
  const [showAspectRatioSelector, setShowAspectRatioSelector] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const resolutionSelectorRef = useRef<HTMLDivElement>(null);
  const aspectRatioSelectorRef = useRef<HTMLDivElement>(null);

  // 点击外部关闭选择器
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (resolutionSelectorRef.current && !resolutionSelectorRef.current.contains(event.target as Node)) {
        setShowResolutionSelector(false);
      }
      if (aspectRatioSelectorRef.current && !aspectRatioSelectorRef.current.contains(event.target as Node)) {
        setShowAspectRatioSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 计算实际价格：1080p 双倍剧点
  const basePrice = selectedModel?.coinPrice ?? 0;
  const resolutionMultiplier = formData.resolution === Resolution.FHD_1080P ? 2 : 1;
  const modelPrice = basePrice * resolutionMultiplier;

  const hasEnoughCoins = (account?.balance ?? 0) >= modelPrice;

  const maxImages = selectedModel?.imageInputCount || 4;
  const canAddMore = formData.referenceImages.length < maxImages;

  const removeReferenceImage = (index: number) => {
    onFormDataChange({
      referenceImages: formData.referenceImages.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = (url: string) => {
    if (formData.referenceImages.length < maxImages) {
      onFormDataChange({
        referenceImages: [...formData.referenceImages, url]
      });
    }
    setShowImageUpload(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 手动验证 prompt
    if (!formData.prompt.trim()) {
      return;
    }

    onSubmit(e);
  };

  return (
    <div className="w-96 lg:w-[420px] bg-bg-form border-r border-border-subtle shadow-xl overflow-y-auto">
      <ImageTabs />

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 标题行：重置按钮 */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-300">{t('generation:imageEdit.title')}</span>
            {editingItem && (
              <button
                type="button"
                onClick={onReset}
                className="p-1.5 text-gray-400 hover:text-gray-200 hover:bg-bg-elevated rounded-lg transition-colors"
                title={t('generation:imageEdit.resetForm')}
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* 模型选择 */}
          <ModelSelector
            models={models}
            selectedModelId={formData.modelId}
            onChange={(modelId) => onFormDataChange({ modelId })}
            loading={modelsLoading}
            placeholder={t('generation:form.selectModel')}
          />

          {/* 输入容器 */}
          <div className="bg-bg-surface rounded-2xl p-4 border border-border-subtle transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
            {/* 提示词输入 */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-500">
                  {formData.prompt.length} / 5000
                </span>
              </div>
              <textarea
                value={formData.prompt}
                onChange={(e) => onFormDataChange({ prompt: e.target.value })}
                maxLength={5000}
                rows={4}
                className="w-full bg-transparent border-none focus:ring-0 text-gray-50 placeholder-gray-500 text-base resize-none py-0"
                placeholder={t('generation:imageEdit.promptPlaceholder')}
              />
            </div>

            {/* 参考图片区域 - 固定显示 */}
            <div className="mt-3 pt-3 border-t border-border-subtle relative">
              <div className="flex gap-2 flex-wrap items-center">
                {formData.referenceImages.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={t('generation:imageEdit.referenceImageAlt', { index: index + 1 })}
                      className="w-12 h-12 object-cover rounded-lg border border-border-default cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => setPreviewImage(url)}
                    />
                    <button
                      type="button"
                      onClick={() => removeReferenceImage(index)}
                      className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors"
                    >
                      <XMarkIcon className="h-2.5 w-2.5" />
                    </button>
                  </div>
                ))}

                {/* 添加按钮 */}
                {canAddMore && (
                  <button
                    type="button"
                    onClick={() => setShowImageUpload(true)}
                    className="w-12 h-12 bg-bg-elevated hover:bg-border-default rounded-lg flex items-center justify-center transition-colors border-2 border-dashed border-border-default"
                    title={t('generation:imageEdit.addReferenceImage', { current: formData.referenceImages.length, max: maxImages })}
                  >
                    <PlusIcon className="h-5 w-5 text-gray-400" />
                  </button>
                )}

                {/* 提示文字 */}
                <span className="text-xs text-gray-500">
                  {formData.referenceImages.length === 0 ? t('generation:imageEdit.referenceImageRequired') : `${formData.referenceImages.length}/${maxImages}`}
                </span>
              </div>

              {/* 图片上传弹出面板 */}
              {showImageUpload && canAddMore && (
                <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 p-3 min-w-[280px]">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">
                      {t('generation:imageEdit.addReferenceImage', { current: formData.referenceImages.length, max: maxImages })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowImageUpload(false)}
                      className="text-gray-500 hover:text-gray-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <ImageUpload
                    onUploadSuccess={handleImageUpload}
                    showAssets={true}
                    models={models}
                  />
                </div>
              )}
            </div>

            {/* 底部工具栏 */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border-subtle flex-wrap">
              {/* 分辨率选择 */}
              <div className="relative" ref={resolutionSelectorRef}>
                <button
                  type="button"
                  onClick={() => setShowResolutionSelector(!showResolutionSelector)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors"
                >
                  <PhotoIcon className="w-3.5 h-3.5 text-purple-400" />
                  <span>{resolutionLabels[formData.resolution]}</span>
                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                </button>

                {showResolutionSelector && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1 min-w-[140px]">
                    {Object.entries(resolutionLabels).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          const newResolution = value as Resolution;
                          onFormDataChange({ resolution: newResolution });
                          onResolutionChange?.(newResolution);
                          setShowResolutionSelector(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${formData.resolution === value ? 'text-primary bg-bg-elevated' : 'text-gray-200'
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 宽高比选择 */}
              <div className="relative" ref={aspectRatioSelectorRef}>
                <button
                  type="button"
                  onClick={() => setShowAspectRatioSelector(!showAspectRatioSelector)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-bg-elevated hover:bg-border-default text-gray-200 text-[13px] rounded-lg border border-border-default transition-colors"
                >
                  <AdjustmentsHorizontalIcon className="w-3.5 h-3.5 text-blue-400" />
                  <span>{formData.aspectRatio}</span>
                  <ChevronDownIcon className="w-3 h-3 text-gray-400" />
                </button>

                {showAspectRatioSelector && (
                  <div className="absolute bottom-full left-0 mb-2 bg-bg-surface border border-border-subtle rounded-lg shadow-lg z-50 py-1 min-w-[160px]">
                    {Object.entries(aspectRatioLabels).map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          onAspectRatioChange(value as AspectRatio);
                          setShowAspectRatioSelector(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-bg-elevated transition-colors ${formData.aspectRatio === value ? 'text-primary bg-bg-elevated' : 'text-gray-200'
                          }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 生成按钮和剧点 */}
          <div className="pt-4 space-y-2">
            <button
              type="submit"
              disabled={modelsLoading || formData.referenceImages.filter(img => img.trim()).length === 0 || !hasEnoughCoins || !formData.modelId}
              className="w-full bg-gradient-to-br from-primary to-primary-light hover:shadow-[0_0_20px_rgba(255,92,0,0.3)] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              {modelsLoading ? t('common_ui:loading') : t('generation:imageEdit.submitButton')}
            </button>
            {!modelsLoading && modelPrice > 0 && (
              <div className="flex items-center justify-between text-xs px-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CurrencyDollarIcon className="w-3.5 h-3.5" />
                  <span>{t('generation:imageEdit.cost')} <span className="font-mono text-primary">{modelPrice}</span></span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-gray-500">{t('generation:imageEdit.balance')}</span>
                  <span className={`font-mono ${hasEnoughCoins ? 'text-gray-300' : 'text-error'}`}>
                    {account?.balance ?? 0}
                  </span>
                </div>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* 图片预览弹窗 */}
      <MediaPreviewModal
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        type="image"
        src={previewImage || ''}
        alt={t('generation:imageEdit.referenceImagePreview')}
      />
    </div>
  );
}
